/**
 * The groups of the specialist's students, in consultation (#1445): which group, which week, and
 * the specialist's lean answer read into the inputs of the teacher's own « Groupe › Présences » grid
 * (group-attendance-grid.vue), drawn read only. One grid for the two readers: the charter has a
 * single canonical matrix of students by days, not a second one for the specialist.
 *
 * Pure, tested in specialist-groups.test.ts.
 */
import { translate, type Holiday, type SchedulePatternPeriod } from '@fondationcss/sdk';
import type { Specialist, SpecialistGroup, SpecialistGroupAttendance, SpecialistGroupProgression } from '@/sdk/dto/specialist';
import type { Frequentation, Group, GroupMembership, GroupModuleStats, LearningModule, Presence } from '@/sdk/model';
import { GroupStatus } from '@/sdk/model/group';
import { FrequentationStatus, PresenceType } from '@/sdk/model';
import { addDays, formatDateForInput, getSundayOfWeek, parseDateStringAsLocal } from '@/utils/date-utils';
import { formatDateRangeShort } from '@/utils/format';

/** Its centre, and how many of its students are the reader's: the line under its name in the menu. */
export function groupSubtitle(group: Pick<SpecialistGroup, 'siteName' | 'myStudentCount'>): string {
  return [group.siteName, translate('parcours.specialist.groups.myStudents', { count: group.myStudentCount })]
    .filter(Boolean).join(' · ');
}

/**
 * The context bar's « Groupe » menu on the specialist's routes: a section per program, by title, and
 * its groups by name. The teacher's bar names the program in an axis of its own; the specialist's
 * has none, so the headings say it.
 */
export function specialistGroupMenuSections<T extends Pick<SpecialistGroup, 'name' | 'programTitle'>>(
  groups: readonly T[],
): { label: string; entries: T[] }[] {
  const byProgram = new Map<string, T[]>();
  for (const group of groups) byProgram.set(group.programTitle, [...(byProgram.get(group.programTitle) ?? []), group]);
  return [...byProgram.entries()]
    .sort(([a], [b]) => a.localeCompare(b, 'fr'))
    .map(([label, entries]) => ({
      label,
      entries: [...entries].sort((a, b) => a.name.localeCompare(b.name, 'fr', { numeric: true })),
    }));
}

/**
 * The group to show once the list is read: the first of `candidates` still one of the reader's — the
 * one the address names, the one on screen, the one remembered —, else the first of the list.
 */
export function pickSpecialistGroup(
  groups: readonly Pick<SpecialistGroup, 'id'>[],
  ...candidates: (string | null | undefined)[]
): string | null {
  const held = candidates.find(candidate => !!candidate && groups.some(group => group.id === candidate));
  return held ?? groups[0]?.id ?? null;
}

/** The week holding `date`, Sunday to Saturday as the teacher's grid reads it. */
export function weekOf(date: string): { from: string; to: string } {
  const sunday = getSundayOfWeek(parseDateStringAsLocal(date));
  return { from: formatDateForInput(sunday), to: formatDateForInput(addDays(sunday, 6)) };
}

/** The week `weeks` away from the one starting on `from`. */
export function shiftWeek(from: string, weeks: number): { from: string; to: string } {
  return weekOf(formatDateForInput(addDays(parseDateStringAsLocal(from), weeks * 7)));
}

/**
 * The two weeks the grid shows, as the teacher's « Groupe › Présences » does: the week before the
 * chosen one, and the chosen one — its days to come hatched.
 */
export function attendanceWindow(weekFrom: string): { from: string; to: string } {
  return { from: shiftWeek(weekFrom, -1).from, to: shiftWeek(weekFrom, 0).to };
}

/** « 11 oct. – 24 oct. »: the window, as the teacher's page names it. */
export function windowLabel(window: { from: string; to: string }): string {
  return formatDateRangeShort(parseDateStringAsLocal(window.from), parseDateStringAsLocal(window.to));
}

/** The grid stops at today, like the teacher's: no week starting after it. */
export function canShowNextWeek(from: string, today: string): boolean {
  return shiftWeek(from, 1).from <= today;
}

/** The id the reader's own bookings are credited to in the grid's inputs: the answer names nobody. */
export const SELF_ID = 'self';

export interface GroupGridInputs {
  students: Frequentation[];
  groups: Group[];
  groupMemberships: GroupMembership[];
  presences: Presence[];
  periods: SchedulePatternPeriod[];
  periodIdsBySubject: Map<string, Set<string>>;
  holidays: Holiday[];
  /** The reader, so the grid names the periods booked with them. */
  specialists: Specialist[];
  /** Their students, drawn apart in the list. */
  highlighted: Set<string>;
}

const WEEKDAYS = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'] as const;

function minutesToTime(minutes: number): string {
  const hours = Math.floor(minutes / 60);
  return `${String(hours).padStart(2, '0')}:${String(minutes % 60).padStart(2, '0')}`;
}

/**
 * The grid's inputs from the specialist's answer. A column runs on a weekday when one of the days of
 * the answer lists it; a day the answer gives no column, under a holiday's name, is that holiday. A
 * cell is one presence: a booking credited to the reader names them, one with another specialist
 * names nobody — « Avec un spécialiste », as the answer says nothing more of it.
 */
export function groupGridInputs(
  answer: SpecialistGroupAttendance,
  reader: { firstName: string; lastName: string },
): GroupGridInputs {
  const groupId = answer.group.id;
  const students = answer.students.map(student => ({
    id: student.frequentationId,
    studentId: '',
    student: { id: '', user: { id: '', firstName: student.firstName, lastName: student.lastName, email: '' } },
    learningProgramId: '',
    startDate: answer.from,
    status: FrequentationStatus.EnCours,
  }) as unknown as Frequentation);

  const weekdays = new Map<string, Set<number>>();
  for (const day of answer.days) {
    const weekday = parseDateStringAsLocal(day.date).getDay();
    for (const periodId of day.periodIds) {
      weekdays.set(periodId, (weekdays.get(periodId) ?? new Set()).add(weekday));
    }
  }
  const periods = answer.periods.map(period => ({
    id: period.id,
    name: period.name,
    startTime: minutesToTime(period.startMinutes),
    durationMinutes: period.durationMinutes,
    ...Object.fromEntries(WEEKDAYS.map((key, index) => [key, weekdays.get(period.id)?.has(index) ?? false])),
  }) as SchedulePatternPeriod);
  const allPeriodIds = new Set(answer.periods.map(period => period.id));

  return {
    students,
    groups: [{ id: groupId, name: answer.group.name } as Group],
    groupMemberships: answer.students.map(student => ({
      id: `${groupId}|${student.frequentationId}`,
      groupId,
      frequentationId: student.frequentationId,
    }) as GroupMembership),
    presences: answer.cells.map(cell => ({
      id: `${cell.frequentationId}|${cell.date}|${cell.periodId}`,
      frequentationId: cell.frequentationId,
      date: cell.date,
      periodId: cell.periodId,
      status: cell.status,
      presenceType: cell.withSpecialist ? PresenceType.SpecialistSupport : PresenceType.Normal,
      teacherId: cell.withMe ? SELF_ID : null,
      learningModuleId: '',
      effectiveHours: 0,
    }) as unknown as Presence),
    periods,
    periodIdsBySubject: new Map(answer.students.map(student => [student.frequentationId, allPeriodIds])),
    holidays: answer.days
      .filter(day => day.holidayName && day.periodIds.length === 0)
      .map(day => ({ id: `holiday-${day.date}`, name: day.holidayName!, startDate: day.date, endDate: day.date, calendarYearId: '' }) as Holiday),
    specialists: [{ teacherId: SELF_ID, firstName: reader.firstName, lastName: reader.lastName, specialistType: '' }],
    highlighted: new Set(answer.students.filter(student => student.isMine).map(student => student.frequentationId)),
  };
}

/**
 * The progression answer read into what the teacher's « Progression du groupe » holds from its four
 * routes: the group, its program's competencies with their place in the sequence, and the counts.
 */
export function progressionGroup(answer: SpecialistGroupProgression): {
  group: Group;
  stats: GroupModuleStats[];
  memberCount: number;
  modules: LearningModule[];
  sequenceOrder: Record<string, number>;
} {
  const group = answer.group;
  return {
    group: {
      id: group.id,
      name: group.name,
      status: GroupStatus.Confirmed,
      learningProgram: { title: group.programTitle, code: group.programCode, isIndividual: false },
      planning: group.planningStartDate ? { startDate: group.planningStartDate } : undefined,
      site: group.siteName ? { name: group.siteName } : undefined,
    } as unknown as Group,
    stats: answer.stats,
    memberCount: answer.memberCount,
    modules: answer.modules.map(module => ({
      id: module.id,
      number: module.number,
      title: module.title,
      code: module.code,
      numberOfHours: module.numberOfHours,
    }) as LearningModule),
    sequenceOrder: Object.fromEntries(answer.modules
      .filter(module => module.sequenceOrder !== null && module.sequenceOrder !== undefined)
      .map(module => [module.id, module.sequenceOrder!])),
  };
}
